// PRD 14 I5 — one support action of Tenis10 Claims, confirmed in a dialog with the admin's mandatory note (what the parent
// asked, what support checked — saved in the activity log): the Claim into an account (a child under 16 or a 16+ history),
// the cancel of a Claim, the move to another account, support's hide, the show-again and the family link resent. The texts
// are the I0 simulation's (cp-*-modal) and the hide's sketch Tudor approved (2026-10-16). PRD 16 LR12: the passport support
// activates (the LR10 canvas, D-B) — from a day it chooses to 31 December. RELEASE-TENIS10 § 11 P5: the correction of a
// child's data, which the parent reads only (the six fields, from the account's own copy). P55: the family link sent to a new
// email the parent gives, once support checked the parent's identity — every later email of the family goes there. P106
// (canvas „P106 · Județul copilului”, Tudor 2026-10-27, Î4 A): the child's place is ONE city field (Google, cities only) that
// reads „Tecuci, Galați, Romania” and fills the county and the country itself, in place of „City” + „Country”.
import React, { useRef, useState } from "react"
import SearchBox from "components/Common/SearchBox"
import DateTimeField from "components/Common/DateTimeField"
import moment from "moment-timezone"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import {
  activatePassport, cancelClaim, claimProfile, editChildData, hideProfile, lookupAccounts, moveProfile, resendFamily, sendFamilyToEmail, showProfile,
} from "./api"
import { LEVELS, accountLabel, errorText, refusalFor } from "./claimsLabels"
import { programToday, programYear, supportPassportDay } from "./passportLabels"
import ChildPlacePicker from "./ChildPlacePicker"
import { placeText } from "./childPlace"

export const NOTE_MIN = 3
export const NOTE_MAX = 1000
const childName = child => `${child.firstName} ${child.lastName}`

const STATUS_BADGE = { activated: ["success", "Activated"], pending: ["warning", "Pending"], blocked: ["danger", "Blocked"], deleted: ["danger", "Deleted"] }

/** An account found by its exact ID or address, and whether it can receive the child (an activated player account). */
function AccountPicker({ id, label, hint, value, onChange, disabled }) {
  const [text, setText] = useState("")
  const [state, setState] = useState({ status: "idle" })
  // the text the admin sees: an answer for an older text is dropped
  const latest = useRef("")
  const find = async () => {
    const q = text.trim()
    if (!q || state.status === "loading") return
    setState({ status: "loading" })
    onChange(null)
    try {
      const { accounts = [] } = await lookupAccounts(q)
      if (latest.current.trim() !== q) return undefined
      if (!accounts.length) return setState({ status: "none" })
      setState({ status: "found" })
      return onChange(accounts[0])
    } catch (error) {
      if (latest.current.trim() !== q) return undefined
      return setState({ status: "error", message: errorText(error, "The account could not be looked up. Try again.") })
    }
  }
  const usable = value && value.role === "player" && value.status === "activated"
  const [tone, statusText] = STATUS_BADGE[value?.status] || ["secondary", value?.status]
  return <div className="form-group">
    <label htmlFor={id} className="mb-1 font-weight-medium">{label} <span className="text-danger">*</span></label>
    <div className="d-flex tenis10-claims-picker">
      <SearchBox id={id} className="flex-grow-1" value={text} disabled={disabled} maxLength={254} placeholder="#71533 or the account's email"
        onValueChange={next => { latest.current = next; setText(next); onChange(null); setState({ status: "idle" }) }}
        onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); find() } }} />
      <button type="button" className="btn btn-light ml-2" disabled={disabled || !text.trim() || state.status === "loading"} onClick={find}>
        {state.status === "loading" ? "Finding…" : "Find"}
      </button>
    </div>
    <small className="text-muted d-block">Enter the account's ID (#71533) or its exact email address.</small>
    {state.status === "none" && <p role="alert" className="text-danger mb-0 mt-1">No Sportya account has this ID or address.</p>}
    {state.status === "error" && <p role="alert" className="text-danger mb-0 mt-1">{state.message}</p>}
    {value && <div className="tenis10-claims-account mt-2" data-testid="picked-account">
      <div className="font-weight-medium">{accountLabel(value)}</div>
      <div className="text-muted font-size-12">{value.email}</div>
      <span className={`badge badge-soft-${tone} mr-1`}>{statusText}</span>
      {usable ? <small className="text-muted">{hint}</small>
        : <small className="text-danger">{value.role !== "player" ? "Only a player account can receive a child." : "Only an activated account can receive a child."}</small>}
    </div>}
  </div>
}

const NoteField = ({ value, onChange, disabled }) => <div className="form-group mb-0">
  <label htmlFor="tenis10-claims-note" className="mb-1 font-weight-medium">
    Note <span className="text-danger">*</span> <span className="text-muted font-weight-normal">(required, saved in the activity log)</span>
  </label>
  <textarea id="tenis10-claims-note" className="form-control" rows={3} maxLength={NOTE_MAX} value={value} disabled={disabled}
    aria-describedby="tenis10-claims-note-rule" onChange={event => onChange(event.target.value)} />
  <small id="tenis10-claims-note-rule" className="text-muted">At least {NOTE_MIN} characters. Write what the parent asked and what you checked.</small>
</div>

const Explain = ({ tone = "", children }) => <p className={`tenis10-claims-explain ${tone}`}>{children}</p>

// RELEASE-TENIS10 § 11 P55 — the new address support types (the API's rule): one run without spaces, a name, `@`, a domain with a dot
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const addressOf = text => text.trim().toLowerCase()
/** Why `text` cannot be the family's new address, or null: empty, not an address, or one the family's emails already use. */
export function newEmailProblem(text, family) {
  const email = addressOf(text)
  if (!email) return "empty"
  if (email.length > 254 || !EMAIL.test(email)) return refusalFor("SUPPORT_EMAIL_INVALID")
  const own = [family?.contact?.email, family?.delivery?.email].filter(Boolean).map(addressOf)
  return own.includes(email) ? refusalFor("SUPPORT_EMAIL_SAME") : null
}

/** The address the family's emails go to (P55: the one support set, once set), as the dialogs name it. */
const familyEmail = family => family?.emailsTo?.email || family?.contact?.email

const NewEmailField = ({ value, problem, onChange, disabled }) => <div className="form-group">
  <label htmlFor="tenis10-claims-new-email" className="mb-1 font-weight-medium">New email address <span className="text-danger">*</span></label>
  <input id="tenis10-claims-new-email" type="email" className="form-control" value={value} disabled={disabled} maxLength={254} autoComplete="off"
    aria-describedby="tenis10-claims-new-email-rule" aria-invalid={problem && problem !== "empty" ? true : undefined}
    onChange={event => onChange(event.target.value)} />
  <small id="tenis10-claims-new-email-rule" className="text-muted d-block">The address the parent gave you. It must differ from the address the family's emails use now.</small>
  {problem && problem !== "empty" && <p role="alert" className="text-danger mb-0 mt-1">{problem}</p>}
</div>

const fullDay = day => moment.utc(day, "YYYY-MM-DD").format("D MMM YYYY")

/** The passport's first day and the window it gives (D-B): today by default; a day the rule refuses says why. */
const PassportFields = ({ day, range, onChange, disabled }) => <>
  <div className="form-group">
    <label htmlFor="tenis10-passport-from" className="mb-1 font-weight-medium">Active from <span className="text-danger">*</span></label>
    <DateTimeField id="tenis10-passport-from" mode="date" value={day} disabled={disabled} onChange={event => onChange(event.target.value)} />
    <small className="text-muted d-block">Today or a past date of this year (a passport paid earlier); 1 January for next year.</small>
  </div>
  <div className="tenis10-claims-account mb-3">
    {range
      ? <div className="font-weight-medium">Passport {range.year} · {fullDay(range.startDate)} – {fullDay(range.endDate)}</div>
      : <p role="alert" className="text-danger mb-1">{refusalFor("SUPPORT_PASSPORT_DAY_INVALID")}</p>}
    <div className="text-muted font-size-12">A passport always ends on 31 December. Activated by support: no payment in Sportya; the note and your name go into the activity log.</div>
  </div>
</>

// RELEASE-TENIS10 § 11 P5 — the child's data as support corrects it: from the account's own copy (`child.data`), every field
// filled; only the fields that change are sent, and the API applies the rules of an add. P106: the place is the city with its
// county and its country (a copy may lack the county or even the country — the place then reads as it is, and saving any
// other correction never needs it)
const DATA_TEXT = ["firstName", "lastName"]
const childValues = child => {
  const data = child?.data || {}
  return {
    firstName: data.firstName ?? child?.firstName ?? "", lastName: data.lastName ?? child?.lastName ?? "", gender: data.gender ?? child?.gender ?? "",
    birthDate: data.birthDate ?? "", tenis10Level: data.tenis10Level ?? child?.tenis10Level ?? "",
    place: data.city ? { city: data.city, county: data.county ?? null, country: data.country ?? "" } : null,
  }
}
const dataComplete = values => DATA_TEXT.every(key => values[key].trim()) && Boolean(values.place && values.gender && values.tenis10Level)
  && /^\d{4}-\d{2}-\d{2}$/.test(values.birthDate)
/** The fields support changed: the names trimmed; the place once support chose a city (Google's code and area, never a county). */
const dataChanges = (initial, values) => {
  const changes = {}
  for (const key of ["firstName", "lastName"]) if (values[key].trim() !== initial[key]) changes[key] = values[key].trim()
  for (const key of ["gender", "birthDate"]) if (values[key] !== initial[key]) changes[key] = values[key]
  if (values.place && values.place !== initial.place && placeText(values.place) !== placeText(initial.place)) {
    const { city, country, countryCode, administrativeArea } = values.place
    changes.location = { city, country, ...(countryCode ? { countryCode } : {}), ...(administrativeArea ? { administrativeArea } : {}) }
  }
  if (values.tenis10Level !== initial.tenis10Level) changes.tenis10Level = values.tenis10Level
  return changes
}

/** The six fields of a child's data, side by side from sm; the city takes a whole row (P106). */
function ChildDataFields({ values, onChange, disabled }) {
  const set = key => event => onChange({ ...values, [key]: event.target.value })
  const input = (key, label, type = "text") => <div className="form-group col-sm-6">
    <label htmlFor={`tenis10-child-${key}`} className="mb-1 font-weight-medium">{label} <span className="text-danger">*</span></label>
    <input id={`tenis10-child-${key}`} className="form-control" type={type} value={values[key]} disabled={disabled} maxLength={type === "text" ? 100 : undefined}
      onChange={set(key)} />
  </div>
  return <div className="form-row">
    {input("firstName", "First name")}
    {input("lastName", "Last name")}
    <div className="form-group col-sm-6">
      <label htmlFor="tenis10-child-gender" className="mb-1 font-weight-medium">Gender <span className="text-danger">*</span></label>
      <select id="tenis10-child-gender" className="form-control" value={values.gender} disabled={disabled} onChange={set("gender")}>
        <option value="female">Girl</option>
        <option value="male">Boy</option>
      </select>
    </div>
    <div className="form-group col-sm-6">
      <label htmlFor="tenis10-child-birthDate" className="mb-1 font-weight-medium">Date of birth <span className="text-danger">*</span></label>
      <DateTimeField id="tenis10-child-birthDate" mode="date" value={values.birthDate} disabled={disabled} onChange={set("birthDate")}
        aria-describedby="tenis10-child-birthDate-rule" />
      <small id="tenis10-child-birthDate-rule" className="text-muted">4 to 15 by 31 December: only the year counts.</small>
    </div>
    <ChildPlacePicker id="tenis10-child-place" label="City (county and country filled in)" value={values.place} disabled={disabled}
      onChange={place => onChange({ ...values, place })} />
    <div className="form-group col-sm-6">
      <label htmlFor="tenis10-child-tenis10Level" className="mb-1 font-weight-medium">Tenis10 level <span className="text-danger">*</span></label>
      <select id="tenis10-child-tenis10Level" className="form-control" value={values.tenis10Level} disabled={disabled} onChange={set("tenis10Level")}
        aria-describedby="tenis10-child-level-rule">
        {Object.entries(LEVELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select>
      <small id="tenis10-child-level-rule" className="text-muted">Its own level or a higher one: Roșu up to 8, Portocaliu up to 9, Verde up to 10, Galben up to 15.</small>
    </div>
  </div>
}

/** Where a claim's confirmation goes: the tenis10.ro address, the new one support set (P55) — or, from an account's page, the family's. */
const confirmationTo = family => (!family ? "the family's address" : family.delivery ? "the family's new address" : "the tenis10.ro address")

/** Each action: its title, its button, its tone, whether it needs an account, its texts and its request. */
const ACTIONS = {
  claim: ({ child, family }) => ({
    title: child.adult ? `Claim ${childName(child)} (16+) into the player's account` : `Claim ${childName(child)} into a Sportya account`,
    confirm: "Confirm claim",
    account: { label: "Sportya account", hint: child.adult ? "Player account." : "The Parent persona will be added." },
    body: () => null,
    explain: child.adult
      ? <Explain>No birth-date check (many accounts made by coaches and organizers carry a wrong one): your note is the proof.</Explain>
      : <Explain>Under 16 the system checks no identity: your note is the proof. A confirmation goes to {confirmationTo(family)}, as for every claim.</Explain>,
    send: ({ account, note, bindFamily }) => claimProfile(child.profileId, { userId: account.userId, note, bindFamily }),
    done: answer => `${childName(child)} was claimed into the account.${answer?.data?.confirmation === "sent" ? ` The confirmation went to ${confirmationTo(family)}.` : ""}`,
  }),
  cancelClaim: ({ child }) => ({
    title: `Cancel the claim of ${childName(child)}`,
    confirm: "Cancel claim",
    tone: "danger",
    body: () => <>
      <p>{child.firstName} leaves account <strong>{accountLabel(child.claim?.account)}</strong> and becomes an Unclaimed profile again, with all the history. The claim stays in the profile's claim history.</p>
      <Explain>If this account holds no other child of the family, the family link is unbound from it, so the right parent can use it. The same rule applies when a child is moved out or an account is deleted.</Explain>
      {child.state === "hidden" && <Explain tone="warn">{child.firstName} stays hidden. Show the profile again afterwards if the family asks.</Explain>}
    </>,
    send: ({ note }) => cancelClaim(child.profileId, { note }),
    done: `The claim of ${childName(child)} was cancelled.`,
  }),
  move: ({ child }) => ({
    title: `Move ${childName(child)} to another account`,
    confirm: "Move child",
    account: { label: "Target Sportya account", hint: "The Parent persona will be added." },
    body: () => <p>{child.firstName} goes into Copiii mei of the target account, with all the history and the passport{child.state === "hidden" ? ", and becomes visible again there" : ""}. The current account <strong>{accountLabel(child.claim?.account)}</strong> lets the child go.</p>,
    send: ({ account, note }) => moveProfile(child.profileId, { userId: account.userId, note }),
    done: `${childName(child)} was moved to the new account.`,
  }),
  hide: ({ child }) => ({
    title: `Hide ${childName(child)}`,
    confirm: "Hide profile",
    tone: "danger",
    body: () => <p>{child.firstName} disappears from Sportya at once: the page closes, the profile leaves the search and “Jucători Tenis10”, and other children's history shows “{child.firstName} {child.lastName.charAt(0)}.” without a link. On the family's claim page the profile stays hidden and cannot be claimed. Nothing is deleted: the history and the legacy family record stay.</p>,
    explain: <Explain tone="warn">For a parent who cannot use the family link (there, “Nu vreau acest profil pe Sportya” hides without support). No email leaves: answer the parent yourself. Only an admin can show the profile again.</Explain>,
    send: ({ note }) => hideProfile(child.profileId, { note }),
    done: `${childName(child)} is hidden. Nothing was sent to the family.`,
  }),
  show: ({ child }) => ({
    title: `Show ${childName(child)} again`,
    confirm: "Show profile",
    body: () => <p>{child.firstName} was hidden {child.hidden?.by === "parent" ? "by the parent from the family link" : child.hidden?.by === "support" ? "by support" : "with a deleted account"}. Showing the profile again brings back the page, the search and “Jucători Tenis10”, and the full name with a link in other children's history. On the family's claim page it can be claimed again.</p>,
    explain: <Explain>Only an admin can show a profile again. Full deletion is a GDPR request handled by the DPO.</Explain>,
    send: ({ note }) => showProfile(child.profileId, { note }),
    done: `${childName(child)} is visible again.`,
  }),
  // PRD 16 LR12 (LR10 verdict D-B) — nothing is charged in Sportya: the note says how the family paid and what support checked
  activatePassport: ({ child }) => ({
    title: `Activate the passport of ${childName(child)}`,
    confirm: "Activate passport",
    passport: true,
    body: () => <p>{child.firstName} has no passport for {programYear()}. Once it is active, the parent can enter {child.firstName} in Tenis10 competitions. Nothing is charged in Sportya: write in the note how the family paid (for example at the club) and what you checked.</p>,
    send: ({ note, range }) => activatePassport(child.profileId, { startDate: range.startDate, note }),
    done: (answer, { range }) => `The passport of ${childName(child)} is active from ${fullDay(range.startDate)} to ${fullDay(range.endDate)}.`,
  }),
  // RELEASE-TENIS10 § 11 P5 (Tudor, 2026-10-24) — the parent reads the child's data only and asks contact@sportya.net
  editData: ({ child }) => ({
    title: `Correct the data of ${childName(child)}`,
    confirm: "Save corrections",
    childData: true,
    body: () => <p>The parent sees {child.firstName}'s data on Sportya without changing it and asks support at contact@sportya.net. Change only what the parent asked: the other fields stay as they are.</p>,
    explain: <Explain>A new name reaches {child.firstName}'s entries in the events not yet started; a draw already made keeps the name it was made with. The photo and the look stay the parent's to change.</Explain>,
    send: ({ note, changes }) => editChildData(child.profileId, { note, ...changes }),
    done: (answer, { values }) => `The data of ${values.firstName.trim()} ${values.lastName.trim()} was corrected.`,
  }),
  resend: ({ family }) => ({
    title: "Resend the family link",
    confirm: "Resend",
    body: () => <p>The migration invitation goes again to <strong>{familyEmail(family)}</strong>, {family.delivery
      ? "the new address support set for the family" : "the address of the tenis10.ro account"}. Only this address can receive it.</p>,
    explain: <Explain tone="warn">A new link replaces the previous one and is valid for 60 days. The family's binding to an account does not change.</Explain>,
    send: ({ note }) => resendFamily(family.invitationId, { note }),
    done: family.delivery ? "The family link was resent to the family's new address." : "The family link was resent to the tenis10.ro address.",
  }),
  // RELEASE-TENIS10 § 11 P55 (Tudor, 2026-10-25) — the parent no longer reaches the family's mailbox: after support checked the
  // parent's identity, the family link goes to the address the parent gave, and every later email of the family with it
  sendToNewEmail: ({ family }) => ({
    title: "Send the family link to a new email",
    confirm: "Send to the new email",
    newEmail: true,
    body: () => <p>For a parent who no longer has access to <strong>{familyEmail(family) || "the tenis10.ro address"}</strong>. Check the parent's identity first (compare the name and the phone with the legacy family record) and write in the note what you checked.</p>,
    explain: <>
      <Explain tone="warn">A new link replaces the previous one and is valid for 60 days: the old link stops working. The family's binding to an account does not change{family.boundTo
        ? <>: the family is bound to account <strong>{accountLabel(family.boundTo.account)}</strong>, so the link works only from that account.</> : "."}</Explain>
      <Explain>From now on every email of the family goes to the new address: the reminders, a resent link, a new link the parent asks for and the claim confirmations. The tenis10.ro address stays in the legacy family record.</Explain>
    </>,
    send: ({ note, email }) => sendFamilyToEmail(family.invitationId, { email, note }),
    done: (answer, { email }) => `The family link was sent to ${email}. Every email of the family goes there from now on.`,
  }),
}

export default function SupportActionDialog({ action, child = null, family = null, onClose, onDone }) {
  const spec = ACTIONS[action]({ child, family })
  const [note, setNote] = useState("")
  const [account, setAccount] = useState(null)
  // a free family is bound to the account its first child under 16 is claimed into (G2) — support chooses
  const canBind = action === "claim" && family && !family.boundTo && child && !child.adult
  const [bindFamily, setBindFamily] = useState(Boolean(canBind))
  const [state, setState] = useState({ status: "idle" })
  // the passport's first day (LR12): today, Romanian time, until support picks another
  const [day, setDay] = useState(() => programToday())
  const range = spec.passport ? supportPassportDay(day) : null
  // the child's data (P5): the account's copy, and what support changed in it
  const [initial] = useState(() => childValues(child))
  const [values, setValues] = useState(initial)
  const changes = spec.childData ? dataChanges(initial, values) : null
  const dataReady = !spec.childData || (dataComplete(values) && Object.keys(changes).length > 0)
  // the family's new address (P55)
  const [email, setEmail] = useState("")
  const emailProblem = spec.newEmail ? newEmailProblem(email, family) : null
  // one request per confirmation, however fast the button is pressed twice
  const sending = useRef(false)
  const pending = state.status === "pending"
  const accountReady = !spec.account || (account && account.role === "player" && account.status === "activated")
  const ready = note.trim().length >= NOTE_MIN && accountReady && (!spec.passport || Boolean(range)) && dataReady && !emailProblem && !pending
  const submit = async () => {
    if (sending.current) return
    sending.current = true
    setState({ status: "pending" })
    try {
      const answer = await spec.send({ note: note.trim(), account, bindFamily: canBind && bindFamily, range, changes, email: addressOf(email) })
      onDone(typeof spec.done === "function" ? spec.done(answer, { range, values, email: addressOf(email) }) : spec.done)
    } catch (error) {
      sending.current = false
      setState({ status: "error", message: errorText(error, "The action could not be completed. Try again; if it fails again, reload the page.") })
    }
  }
  return <Modal isOpen toggle={pending ? undefined : onClose} backdrop={pending ? "static" : true} keyboard={!pending}
    className="tenis10-claims-dialog" labelledBy="tenis10-claims-dialog-title">
    <ModalHeader toggle={pending ? undefined : onClose} id="tenis10-claims-dialog-title">{spec.title}</ModalHeader>
    <ModalBody>
      {spec.body()}
      {spec.account && <AccountPicker id="tenis10-claims-account" label={spec.account.label} hint={spec.account.hint}
        value={account} onChange={setAccount} disabled={pending} />}
      {canBind && <div className="custom-control custom-checkbox mb-3">
        <input type="checkbox" className="custom-control-input" id="tenis10-claims-bind" checked={bindFamily} disabled={pending}
          onChange={event => setBindFamily(event.target.checked)} />
        <label className="custom-control-label" htmlFor="tenis10-claims-bind">Bind the family link to this account (the link is still free)</label>
      </div>}
      {spec.passport && <PassportFields day={day} range={range} onChange={setDay} disabled={pending} />}
      {spec.childData && <ChildDataFields values={values} onChange={setValues} disabled={pending} />}
      {spec.newEmail && <NewEmailField value={email} problem={emailProblem} onChange={setEmail} disabled={pending} />}
      {spec.explain}
      <NoteField value={note} onChange={setNote} disabled={pending} />
      {state.status === "error" && <p role="alert" className="text-danger mt-3 mb-0">{state.message}</p>}
    </ModalBody>
    <ModalFooter>
      <button type="button" className="btn btn-light" disabled={pending} onClick={onClose}>Close</button>
      <button type="button" className={`btn btn-${spec.tone || "primary"}`} disabled={!ready} onClick={submit}>
        {pending ? "Processing…" : spec.confirm}
      </button>
    </ModalFooter>
  </Modal>
}
